import type { Address, Hash, PublicClient } from 'viem';

import type { TransactionQueueItem } from 'components';
import { isolatedPoolComptrollerAbi, vBep20Abi } from 'libs/contracts';
import { stakingV2Abi } from 'libs/contracts/abis/bittensorPrecompiles';
import { STAKING_V2_ADDRESS } from 'libs/contracts/config/bittensorPrecompiles';

export interface RouterQueuePlanStepBase extends TransactionQueueItem {
  shouldSkip: boolean;
}

export const getRouterQueueStep = <T extends RouterQueuePlanStepBase>(
  plan: readonly T[],
  id: string,
): T => {
  const step = plan.find((item) => item.id === id);

  if (!step) {
    throw new Error(`Router queue step not found: ${id}`);
  }

  return step;
};

// viem's `waitForTransactionReceipt` resolves for both success and reverted
// receipts; status must be inspected explicitly to detect a mined revert.
export const waitForHash = async ({
  hash,
  publicClient,
}: {
  hash: Hash;
  publicClient: PublicClient;
}): Promise<void> => {
  const receipt = await publicClient.waitForTransactionReceipt({ hash });
  if (receipt.status !== 'success') {
    throw new Error(`Transaction ${hash} reverted on-chain (status: ${receipt.status}).`);
  }
};

export const getIsRouterDelegated = async ({
  accountAddress,
  publicClient,
  poolComptrollerAddress,
  routerAddress,
}: {
  accountAddress: Address;
  publicClient: PublicClient;
  poolComptrollerAddress: Address;
  routerAddress: Address;
}): Promise<boolean> =>
  publicClient.readContract({
    address: poolComptrollerAddress,
    abi: isolatedPoolComptrollerAbi,
    functionName: 'approvedDelegates',
    args: [accountAddress, routerAddress],
  });

export const getStakingAllowance = async ({
  accountAddress,
  publicClient,
  routerAddress,
  netuid,
}: {
  accountAddress: Address;
  publicClient: PublicClient;
  routerAddress: Address;
  netuid: bigint;
}): Promise<bigint> =>
  publicClient.readContract({
    address: STAKING_V2_ADDRESS as Address,
    abi: stakingV2Abi,
    functionName: 'allowance',
    args: [accountAddress, routerAddress, netuid],
  });

export const getExchangeRateStored = async ({
  publicClient,
  vTokenAddress,
}: {
  publicClient: PublicClient;
  vTokenAddress: Address;
}): Promise<bigint> =>
  publicClient.readContract({
    address: vTokenAddress,
    abi: vBep20Abi,
    functionName: 'exchangeRateStored',
  });

export const runRouterQueueStep = async <T extends RouterQueuePlanStepBase>({
  step,
  action,
  updateItem,
  publicClient,
}: {
  step: T;
  action: () => Promise<Hash>;
  updateItem: (id: string, status: TransactionQueueItem['status']) => void;
  publicClient: PublicClient;
}): Promise<void> => {
  if (step.shouldSkip) {
    updateItem(step.id, 'skipped');
    return;
  }

  updateItem(step.id, 'pendingSignature');
  const hash = await action();
  updateItem(step.id, 'pendingConfirmation');
  await waitForHash({ hash, publicClient });
  updateItem(step.id, 'success');
};

// Maps the active pending step to 'failed' and cascades to subsequent idle
// steps — but only when a pending step was actually observed in this pass.
// A catch that fires before any step started (e.g. pre-plan probe failure)
// leaves all-idle plans untouched. Use inside `setItems(failedStepStateReducer)`.
export const failedStepStateReducer = (
  currentItems: TransactionQueueItem[],
): TransactionQueueItem[] => {
  let failedActiveStep = false;
  return currentItems.map((item) => {
    if (item.status === 'success' || item.status === 'skipped' || item.status === 'failed') {
      return item;
    }
    if (item.status === 'pendingSignature' || item.status === 'pendingConfirmation') {
      failedActiveStep = true;
      return { ...item, status: 'failed' };
    }
    return failedActiveStep ? { ...item, status: 'failed' } : item;
  });
};
